import React, { useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Collapse } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy } from "@fortawesome/free-solid-svg-icons"
import DataTable from "shared/ui/Table/DataTable/DataTable.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import { resolveTableStyle } from "shared/ui/Table/style/looks"
import { notify } from "shared/ui/Notifications/notify"
import type { TableModel } from "utils/tableModel"
import type { EngineOutputItem } from "types/engineOutput.types"
import type { QueryQueue } from "features/Dashboard/runtime/queryQueue"
import { TraceNumberPanel } from "features/Provenance"
import type { TraceRequest } from "features/Provenance"
import { TOTAL_PREP, TOTAL_TABLE } from "../../compiler/names"
import type { TablePlan } from "../../compiler/table"
import { runTable, runTraceCheck } from "../../runtime/runTable"
import {
  describeTabSpecHelper,
  describeTabSpecScript,
  tabSpecTraceTarget,
} from "../../runtime/traceTarget"
import {
  selectPlan,
  selectPlanKey,
  selectRunStatus,
  selectTable,
  TableRun,
  useTabSpecStore,
  useTabSpecStoreApi,
} from "../../runtime/tabSpecStore"

export interface PreviewProps {
  tableId: string
  queue: QueryQueue | null
}

/** How long the preview waits after the last change before it runs the table again. */
export const PREVIEW_DELAY_MS = 600

/**
 * The warnings of a finished run that its plan did not already give: what
 * the run itself found. A run holds the warnings of the plan it ran, then
 * those of the result (`runTable`); `current`, the plan of the current
 * settings, stands in for a run without its plan (a finished run of the
 * current settings has the same plan).
 */
export const foundByRun = (run: TableRun, current: TablePlan | null): string[] => {
  const planned = run.plan?.warnings ?? current?.warnings ?? []
  if (planned.every((w, i) => run.warnings[i] === w)) return run.warnings.slice(planned.length)
  // Not in that order: each warning of the plan is taken out once.
  const left = [...planned]
  return run.warnings.filter((w) => {
    const at = left.indexOf(w)
    if (at < 0) return true
    left.splice(at, 1)
    return false
  })
}

/**
 * The plan's script as it runs on its own in the console: the Total table
 * and the banner it needs first, both removed at the end. The spec's
 * pipelines and weight are not part of it.
 */
export const consoleScript = (plan: TablePlan): string => {
  const total = plan.script.includes(TOTAL_TABLE)
  const banner = plan.banner?.script ? plan.banner : null
  return [
    ...(total ? [TOTAL_PREP] : []),
    ...(banner?.script ? [banner.script] : []),
    plan.script,
    ...(banner ? [`REMOVE ${banner.name}`] : []),
    ...(total ? [`REMOVE ${TOTAL_TABLE}`] : []),
  ].join("\n")
}

const ScriptBlock: React.FC<{ script: string }> = ({ script }) => {
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(script)
      notify.toast.success("Copied to clipboard")
    } catch {
      notify.toast.error("Couldn't copy to clipboard")
    }
  }
  return (
    <div className="ts-script">
      <p className="ts-hint">
        Runs on its own in the console. The tab spec&apos;s pipelines and weight are not part of it.
      </p>
      <pre className="ts-script__code" aria-label="Script">
        {script}
      </pre>
      <Button
        size="small"
        icon={<FontAwesomeIcon icon={faCopy} />}
        onClick={() => void copy()}
      >
        Copy
      </Button>
    </div>
  )
}

/**
 * The table as the tab book will hold it, run through the same `runTable`
 * as the full run. It runs when it mounts and again a moment after the last
 * change of the table's plan, once the session is ready. A finished table is
 * shown only while it is the one the current settings give; while it runs
 * again, or waits to, the previous table stays dimmed under "Updating".
 * While a tab book runs, the book runs this table: the preview starts
 * nothing of its own and looks again once the book has ended. A click on a
 * number of the finished table opens the trace panel: where it comes from,
 * read off the session's lineage.
 */
const Preview: React.FC<PreviewProps> = ({ tableId, queue }) => {
  const store = useTabSpecStoreApi()
  // Only what this table shows is read, so an edit of another table renders nothing here.
  const table = useTabSpecStore((s) => selectTable(s, tableId))
  const tableStyle = useTabSpecStore((s) => s.document.defaults.tableStyle)
  const session = useTabSpecStore((s) => s.session.status)
  const bookActive = useTabSpecStore((s) => s.bookRun?.active === true)
  const run = useTabSpecStore((s) => s.runs[tableId])
  // The status as if the table were in the tab book: the preview shows a
  // table left out of the book too.
  const status = useTabSpecStore((s) => selectRunStatus(s, tableId))
  const style = useMemo(() => resolveTableStyle("report", tableStyle), [tableStyle])
  // Shared with the grid and the runs: compiled once per change of the table's settings.
  const plan = useTabSpecStore((s) => selectPlan(s, tableId))
  const key = useTabSpecStore((s) => selectPlanKey(s, tableId))
  const ready = session === "ready"
  const projectId = useTabSpecStore((s) => s.projectId)
  const instance = useTabSpecStore((s) => s.session.instance)
  const preparation = useTabSpecStore((s) => s.session.preparation)
  /**
   * The cell traced, with the names of the tab spec's tables as they were
   * when it was clicked: read then, so an edit of another table renders
   * nothing here. Its check runs the table's script on the preview's queue;
   * a check that fails removes the temporaries of the plan that ran. The
   * panel reads the session's lineage on the same queue, so the read never
   * meets a run of the session.
   */
  const [trace, setTrace] = useState<{
    request: TraceRequest
    describeHelper: (name: string) => string | undefined
    describeScript: (script: string) => string | undefined
    runCheck?: (script: string) => Promise<EngineOutputItem[]>
  } | null>(null)
  const openTrace = (request: TraceRequest, ran: TablePlan): void => {
    const titles = new Map(
      store.getState().document.tables.map((t) => [t.id, t.title?.trim() || t.question])
    )
    setTrace({
      request,
      describeHelper: describeTabSpecHelper(titles),
      describeScript: describeTabSpecScript(titles),
      runCheck: queue ? (script) => runTraceCheck(queue, ran.temps, script) : undefined,
    })
  }

  // The last table shown, kept for while the next one is on its way.
  const [previous, setPrevious] = useState<TableModel | null>(null)
  useEffect(() => {
    if (status === "done" && run?.model) setPrevious(run.model)
  }, [status, run])

  /** Plan keys of the runs this preview started that have not ended yet. */
  const inFlight = useRef(new Map<string, number>())
  /** The delay after an edit, while it runs. */
  const delayTimer = useRef<number | null>(null)
  /** The check after a run ended or landed, or the tab book ended, while it waits. */
  const checkTimer = useRef<number | null>(null)
  /** Whether the waiting check follows the end of a tab book. */
  const checkAfterBook = useRef(false)
  /** Bumped when the table changes or the preview goes away: callbacks of before then do nothing. */
  const epoch = useRef(0)

  // What follows reads the store when it runs, never what a render saw.

  /** The key of the table's plan as the store holds it now; null without a plan. */
  const keyNow = (): string | null => selectPlanKey(store.getState(), tableId)
  /** A queue, a ready session and no tab book running. */
  const mayRun = (): boolean => {
    const s = store.getState()
    return queue !== null && s.session.status === "ready" && s.bookRun?.active !== true
  }
  /** Whether a run of the plan `runKey` is on its way: started here, or running in the store. */
  const underWay = (runKey: string): boolean => {
    const current = store.getState().runs[tableId]
    return (
      inFlight.current.has(runKey) || (current?.status === "running" && current.key === runKey)
    )
  }
  /**
   * Starts a run of the current plan. `recheck`: once it has ended, look
   * again whether the table was left behind. A run started by that look does
   * not look again, so no run can start another without end.
   */
  const start = (runKey: string, recheck: boolean): void => {
    if (!queue) return
    const own = epoch.current
    const flights = inFlight.current
    flights.set(runKey, (flights.get(runKey) ?? 0) + 1)
    void Promise.resolve(runTable(store, queue, tableId)).finally(() => {
      const left = (flights.get(runKey) ?? 1) - 1
      if (left > 0) flights.set(runKey, left)
      else flights.delete(runKey)
      if (recheck && epoch.current === own) actions.current.scheduleCheck(false)
    })
  }
  /** The first chance, and the end of the delay after an edit: runs unless the table is done. */
  const go = (): void => {
    const runKey = keyNow()
    if (runKey === null || !mayRun() || underWay(runKey)) return
    if (selectRunStatus(store.getState(), tableId) === "done") return
    start(runKey, true)
  }
  /**
   * After a run ended or landed: when the table is stale for the current
   * plan, nothing of it is on its way and no newer edit waits for its delay,
   * one run of the current plan. After a tab book: also when it never ran.
   */
  const check = (afterBook: boolean): void => {
    const runKey = keyNow()
    if (runKey === null || !mayRun() || delayTimer.current !== null || underWay(runKey)) return
    const now = selectRunStatus(store.getState(), tableId)
    if (now === "stale" || (afterBook && now === "idle")) start(runKey, false)
  }
  /**
   * Looks again once the bookkeeping has settled: a run's result reaches the
   * store a moment before its own `finally` has run, so the look waits for
   * a timer. Several requests in a row make one look.
   */
  const scheduleCheck = (afterBook: boolean): void => {
    checkAfterBook.current = checkAfterBook.current || afterBook
    if (checkTimer.current !== null) return
    checkTimer.current = window.setTimeout(() => {
      checkTimer.current = null
      const book = checkAfterBook.current
      checkAfterBook.current = false
      actions.current.check(book)
    }, 0)
  }
  // Timers and runs call the functions of the latest render.
  const actions = useRef({ go, check, scheduleCheck, start })
  useEffect(() => {
    actions.current = { go, check, scheduleCheck, start }
  })

  // A new table, or none: what was waiting for the one before does nothing.
  const started = useRef(false)
  useEffect(
    () => () => {
      epoch.current += 1
      started.current = false
      inFlight.current = new Map()
      if (checkTimer.current !== null) window.clearTimeout(checkTimer.current)
      checkTimer.current = null
      checkAfterBook.current = false
    },
    [tableId]
  )

  // The first chance to run is taken at once; later changes wait until the
  // settings have been still for a moment.
  useEffect(() => {
    if (!queue || !ready || key === null) return
    if (!started.current) {
      started.current = true
      actions.current.go()
      return
    }
    delayTimer.current = window.setTimeout(() => {
      delayTimer.current = null
      actions.current.go()
    }, PREVIEW_DELAY_MS)
    return () => {
      if (delayTimer.current !== null) window.clearTimeout(delayTimer.current)
      delayTimer.current = null
    }
  }, [queue, ready, key, tableId])

  // A run of earlier settings can land after the run of the current ones (a
  // script the queue has already answered comes back at once), which leaves
  // the table stale with nothing left to run. Only a new run in the store
  // counts here: an edit of the settings waits for the delay above.
  const seenRun = useRef(run)
  useEffect(() => {
    if (seenRun.current === run) return
    seenRun.current = run
    if (status === "stale") actions.current.scheduleCheck(false)
  }, [run, status])

  // The tab book ran every table itself; once it has ended, this one is
  // looked at again.
  const wasBook = useRef(bookActive)
  useEffect(() => {
    if (wasBook.current && !bookActive) actions.current.scheduleCheck(true)
    wasBook.current = bookActive
  }, [bookActive])

  const runAgain = (): void => {
    const runKey = keyNow()
    if (runKey !== null && mayRun()) start(runKey, true)
  }

  const body = ((): React.ReactNode => {
    if (session === "no-data")
      return <p className="ts-hint">Import data to see the numbers.</p>
    if (!ready) return <p className="ts-hint">Waiting for the data session.</p>
    if (!plan) return <p className="ts-hint">Waiting for the questionnaire.</p>
    if (status === "done" && run?.model) {
      // The plan the shown table ran: its script and layout say where each number came from.
      const ran = run.plan ?? plan
      return (
        <div className="ts-preview__table">
          <DataTable
            model={run.model}
            style={style}
            sigDisplay="row"
            layout="content"
            onCellClick={
              ran
                ? (down, across, shown) =>
                    openTrace(
                      { ...tabSpecTraceTarget(ran, down, across), title: ran.title, value: shown },
                      ran
                    )
                : undefined
            }
          />
        </div>
      )
    }
    if (status === "error")
      return (
        <Alert
          type="error"
          showIcon
          message={run?.message ?? "The table could not be run."}
          action={
            <Button size="small" disabled={!queue || bookActive} onClick={runAgain}>
              Run again
            </Button>
          }
        />
      )
    if (status === "skipped")
      return <Alert type="info" showIcon message={run?.message ?? "The table was skipped."} />
    // Waiting to run, or running: the table shown before, dimmed.
    const earlier = run?.model ?? previous
    return (
      <div className="ts-preview__updating">
        <div className="ts-preview__status" role="status">
          {/* The dashboards' small loader, in the accent token of the workspace. */}
          <Loader fontSize={12} color="var(--qc-accent)" />
          <span>Updating</span>
        </div>
        {earlier && (
          <div className="ts-preview__table ts-preview__table--dim" aria-hidden>
            <DataTable model={earlier} style={style} sigDisplay="row" layout="content" />
          </div>
        )}
      </div>
    )
  })()

  const done = status === "done" && ready
  // Under the table: the run's notes, then what the run itself found (the
  // check of a table from the data). The plan's warnings are in the
  // inspector's list above, and are not said twice.
  const notes = done && run ? run.notes : []
  const warnings = done && run ? foundByRun(run, plan) : []
  return (
    <div className="ts-preview">
      {plan && (
        <header className="ts-preview__head">
          <h3 className="ts-preview__title">{plan.title}</h3>
          {plan.status === "run" && (
            <>
              {plan.baseText && <p className="ts-preview__line">{plan.baseText}</p>}
              {plan.filterText && <p className="ts-preview__line">{plan.filterText}</p>}
              <p className="ts-preview__line">{plan.weightText}</p>
            </>
          )}
        </header>
      )}
      {body}
      {notes.length + warnings.length > 0 && (
        <ul className="ts-preview__notes">
          {notes.map((n, i) => (
            <li key={`n${i}`}>{n}</li>
          ))}
          {warnings.map((w, i) => (
            <li key={`w${i}`} className="ts-preview__warning">
              {w}
            </li>
          ))}
        </ul>
      )}
      {table?.note && <p className="ts-preview__note">{table.note}</p>}
      {/* Mounted only while open: it reads the lineage and the questionnaire. */}
      {trace && (
        <TraceNumberPanel
          request={trace.request}
          onClose={() => setTrace(null)}
          instance={instance}
          projectId={projectId}
          describeHelper={trace.describeHelper}
          describeScript={trace.describeScript}
          preparation={preparation}
          runCheck={trace.runCheck}
          runExclusive={queue?.runExclusive}
          onRunAgain={
            queue && !bookActive
              ? () => {
                  setTrace(null)
                  runAgain()
                }
              : undefined
          }
        />
      )}
      {plan && plan.script && (
        <Collapse
          ghost
          size="small"
          className="ts-preview__script"
          items={[
            {
              key: "script",
              label: "Show script",
              children: <ScriptBlock script={consoleScript(plan)} />,
            },
          ]}
        />
      )}
    </div>
  )
}

// Its props rarely change: the inspector's own renders pass it by.
export default React.memo(Preview)
